/** Browser client for the course Worker's JSON API (see schema/api.ts). */
import { ERROR_CODES, type ApiEnvelope, type CourseKitApi, type ErrorCode } from "../schema/api.ts";
import type { AccountPage } from "../schema/index.ts";

export class ApiError extends Error {
  /** HTTP status; 0 when the request never got an answer. */
  readonly status: number;
  readonly code: ErrorCode;

  constructor(message: string, status: number, code: ErrorCode) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

type RequestBody = { json: unknown } | { form: FormData };

/**
 * Reads an API answer: the envelope's data on success, otherwise an ApiError
 * with the envelope's message and code (unknown codes become INTERNAL).
 */
const readEnvelope = async <T>(response: Response): Promise<T> => {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Not JSON (e.g. a proxy error page): reported below as an unexpected response.
  }
  // The Worker's answer is trusted to match the contract; only its shape is checked.
  const envelope =
    typeof body === "object" && body !== null && "ok" in body ? (body as ApiEnvelope<T>) : null;
  if (response.ok && envelope?.ok === true) return envelope.data;
  if (envelope?.ok === false) {
    const code = ERROR_CODES.find((known) => known === envelope.error.code) ?? "INTERNAL";
    throw new ApiError(envelope.error.message, response.status, code);
  }
  throw new ApiError(
    `Unexpected response from the server (${response.status})`,
    response.status,
    "INTERNAL",
  );
};

/** fetch() with cookies for an enveloped JSON API; a network failure is ApiError status 0. */
export const requestJson = async <T>(url: string, init: RequestInit): Promise<T> => {
  let response: Response;
  try {
    response = await fetch(url, { ...init, credentials: "include" });
  } catch {
    throw new ApiError("Could not reach the server", 0, "INTERNAL");
  }
  return readEnvelope<T>(response);
};

export interface ApiClientOptions {
  /** Origin of the course Worker; empty for same-origin `/api`. */
  baseUrl?: string;
}

export const createApiClient = ({ baseUrl = "" }: ApiClientOptions = {}): CourseKitApi => {
  const send = <T>(method: string, path: string, body?: RequestBody): Promise<T> => {
    const headers: Record<string, string> = { accept: "application/json" };
    let payload: BodyInit | undefined;
    if (body && "json" in body) {
      headers["content-type"] = "application/json";
      payload = JSON.stringify(body.json);
    } else if (body) {
      // The browser sets the multipart boundary itself.
      payload = body.form;
    }

    return requestJson<T>(`${baseUrl}${path}`, { method, headers, body: payload });
  };

  return {
    listCourses: () => send("GET", "/api/courses"),
    getCourse: (slug) => send("GET", `/api/courses/${encodeURIComponent(slug)}`),
    getLesson: (courseSlug, lessonSlug) =>
      send(
        "GET",
        `/api/courses/${encodeURIComponent(courseSlug)}/lessons/${encodeURIComponent(lessonSlug)}`,
      ),
    getProgress: (courseSlug) => send("GET", `/api/progress/${encodeURIComponent(courseSlug)}`),
    setLessonProgress: (lessonId, input) =>
      send("PUT", `/api/progress/lessons/${encodeURIComponent(lessonId)}`, { json: input }),
    admin: {
      listCourses: () => send("GET", "/api/admin/courses"),
      createCourse: (input) => send("POST", "/api/admin/courses", { json: input }),
      getCourse: (id) => send("GET", `/api/admin/courses/${encodeURIComponent(id)}`),
      updateCourse: (id, input) =>
        send("PUT", `/api/admin/courses/${encodeURIComponent(id)}`, { json: input }),
      deleteCourse: (id) => send("DELETE", `/api/admin/courses/${encodeURIComponent(id)}`),
      createLesson: (courseId, input) =>
        send("POST", `/api/admin/courses/${encodeURIComponent(courseId)}/lessons`, { json: input }),
      reorderLessons: (courseId, input) =>
        send("PUT", `/api/admin/courses/${encodeURIComponent(courseId)}/lessons/order`, {
          json: input,
        }),
      getLesson: (id) => send("GET", `/api/admin/lessons/${encodeURIComponent(id)}`),
      updateLesson: (id, input) =>
        send("PUT", `/api/admin/lessons/${encodeURIComponent(id)}`, { json: input }),
      deleteLesson: (id) => send("DELETE", `/api/admin/lessons/${encodeURIComponent(id)}`),
      uploadMedia: (file, filename): Promise<{ url: string }> => {
        const form = new FormData();
        form.append("file", file, filename);
        return send("POST", "/api/admin/media", { form });
      },
      listAccounts: ({ q, role, page }): Promise<AccountPage> => {
        const params = new URLSearchParams();
        if (q !== "") params.set("q", q);
        if (role !== undefined) params.set("role", role);
        if (page > 1) params.set("page", String(page));
        const search = params.toString();
        return send("GET", search ? `/api/admin/roles?${search}` : "/api/admin/roles");
      },
      setRole: (userId, input) =>
        send("PUT", `/api/admin/roles/${encodeURIComponent(userId)}`, { json: input }),
    },
  };
};
